import React, { useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { useCart } from "../context/CartContext";
import { formatPrice } from "../utils/formatPrice";
import {
  buildCartLineKey,
  buildDefaultSelectionMap,
  buildSelectedModifiers,
  calculateModifierTotal,
  normalizeModifierSelections,
} from "../utils/modifierUtils";

const PLACEHOLDER_IMAGE =
  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZTllY2VmIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzZjNzU3ZCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkZvdG8gWW9rPC90ZXh0Pjwvc3ZnPg==";

const hasModifierRules = (item) => (item?.modifierGroups || []).length > 0;

const formatModifierDelta = (value) => {
  const amount = Number(value || 0);
  if (amount === 0) {
    return "";
  }

  const formatted = formatPrice(Math.abs(amount));
  return amount > 0 ? `+${formatted}` : `-${formatted}`;
};

const MenuItemCard = ({ item }) => {
  const { addItem } = useCart();
  const [showModal, setShowModal] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const modifierGroups = useMemo(() => item?.modifierGroups || [], [item]);
  const [selectedOptionsByGroup, setSelectedOptionsByGroup] = useState(() => buildDefaultSelectionMap(item?.modifierGroups));

  const handleImageError = (event) => {
    const { currentTarget } = event;
    if (currentTarget.dataset.fallbackApplied === "true") {
      return;
    }

    currentTarget.dataset.fallbackApplied = "true";
    currentTarget.src = PLACEHOLDER_IMAGE;
  };

  const resetState = () => {
    setQuantity(1);
    setSelectedOptionsByGroup(buildDefaultSelectionMap(modifierGroups));
  };

  const handleModalClose = () => {
    setShowModal(false);
    resetState();
  };

  const handleCardClick = () => {
    setShowModal(true);
  };

  const normalizedSelections = useMemo(
    () =>
      normalizeModifierSelections(
        Object.entries(selectedOptionsByGroup).flatMap(([groupId, optionIds]) =>
          (optionIds || []).map((optionId) => ({
            groupId: Number(groupId),
            optionId: Number(optionId),
          }))
        )
      ),
    [selectedOptionsByGroup]
  );

  const selectedModifiers = useMemo(
    () => buildSelectedModifiers(item, normalizedSelections),
    [item, normalizedSelections]
  );
  const modifierTotal = useMemo(
    () => calculateModifierTotal(item, normalizedSelections),
    [item, normalizedSelections]
  );
  const effectiveUnitPrice = Number(item.price || 0) + modifierTotal;

  const selectionError = useMemo(() => {
    for (const group of modifierGroups) {
      const selectedCount = (selectedOptionsByGroup[group.id] || []).length;
      if (group.required && selectedCount < Math.max(1, Number(group.minSelections || 0))) {
        return `${group.name} seçimi gerekli.`;
      }
      if (selectedCount < Number(group.minSelections || 0)) {
        return `${group.name} için en az ${group.minSelections} seçim yapın.`;
      }
      if (selectedCount > Number(group.maxSelections || 0)) {
        return `${group.name} için en fazla ${group.maxSelections} seçim yapabilirsiniz.`;
      }
    }
    return "";
  }, [modifierGroups, selectedOptionsByGroup]);

  const toggleOption = (group, optionId) => {
    setSelectedOptionsByGroup((current) => {
      const existing = current[group.id] || [];
      const maxSelections = Number(group.maxSelections || 1);

      if (maxSelections === 1) {
        return {
          ...current,
          [group.id]: existing.includes(optionId) ? [] : [optionId],
        };
      }

      if (existing.includes(optionId)) {
        return {
          ...current,
          [group.id]: existing.filter((entry) => entry !== optionId),
        };
      }

      if (existing.length >= maxSelections) {
        return current;
      }

      return {
        ...current,
        [group.id]: [...existing, optionId],
      };
    });
  };

  const handleAddToCart = () => {
    const configuredItem = hasModifierRules(item)
      ? {
          ...item,
          price: effectiveUnitPrice,
          basePrice: Number(item.price || 0),
          modifierTotal,
          selectedModifierOptions: normalizedSelections,
          modifiers: selectedModifiers,
          lineKey: buildCartLineKey({
            id: item.id,
            selectedModifierOptions: normalizedSelections,
          }),
        }
      : item;

    for (let index = 0; index < quantity; index += 1) {
      addItem(configuredItem);
    }
    handleModalClose();
  };

  return (
    <>
      <article
        className="guest-menu-card"
        onClick={handleCardClick}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleCardClick();
          }
        }}
        role="button"
        tabIndex={0}
      >
        <div className="guest-menu-image-wrapper">
          <img
            src={item.image_url || PLACEHOLDER_IMAGE}
            className="guest-menu-image"
            alt={item.name}
            onError={handleImageError}
          />
        </div>
        <div className="guest-menu-card-body">
          <h6 className="guest-menu-card-title">{item.name}</h6>
          {item.description ? <p className="guest-menu-card-description">{item.description}</p> : null}
          <div className="guest-price">{formatPrice(item.price)}</div>
        </div>
      </article>

      <Modal show={showModal} onHide={handleModalClose} centered dialogClassName="guest-item-modal">
        <Modal.Header closeButton>
          <Modal.Title>{item.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <img
              src={item.image_url || PLACEHOLDER_IMAGE}
              alt={item.name}
              className="guest-item-modal-image"
              onError={handleImageError}
            />
          </div>
          {item.description ? <p className="guest-item-modal-description">{item.description}</p> : null}
          <div className="guest-item-modal-summary">
            <div>
              <span className="guest-item-modal-label">Birim fiyat</span>
              <span className="guest-item-modal-value">{formatPrice(effectiveUnitPrice)}</span>
            </div>
            <div className="guest-quantity-selector" aria-label="Adet seçici">
              <Button
                type="button"
                className="guest-quantity-button"
                variant="outline-secondary"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                aria-label="Adedi azalt"
              >
                -
              </Button>
              <span className="fw-bold fs-5" aria-live="polite">
                {quantity}
              </span>
              <Button
                type="button"
                className="guest-quantity-button"
                variant="outline-secondary"
                onClick={() => setQuantity(quantity + 1)}
                aria-label="Adedi artır"
              >
                +
              </Button>
            </div>
          </div>

          {hasModifierRules(item) ? (
            <div className="guest-modifier-groups mt-3">
              {modifierGroups.map((group) => {
                const selectedOptionIds = selectedOptionsByGroup[group.id] || [];

                return (
                  <section key={group.id} className="guest-modifier-group">
                    <div className="guest-modifier-group-title">{group.name}</div>
                    <div className="guest-modifier-options">
                      {(group.options || []).map((option) => {
                        const selected = selectedOptionIds.includes(option.id);
                        const priceDeltaLabel = formatModifierDelta(option.priceDelta);

                        return (
                          <button
                            key={option.id}
                            type="button"
                            className={`guest-modifier-option ${selected ? "is-selected" : ""}`}
                            onClick={() => toggleOption(group, option.id)}
                          >
                            <span className="guest-modifier-option-indicator" aria-hidden="true" />
                            <span className="guest-modifier-option-main">
                              <span className="guest-modifier-option-name">{option.name}</span>
                              {priceDeltaLabel ? (
                                <span
                                  className={`guest-modifier-option-price ${
                                    Number(option.priceDelta || 0) > 0 ? "is-positive" : "is-negative"
                                  }`}
                                >
                                  {priceDeltaLabel}
                                </span>
                              ) : null}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
            </div>
          ) : null}

          {selectionError ? <div className="text-danger small mt-3">{selectionError}</div> : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleModalClose}>
            Vazgeç
          </Button>
          <Button variant="success" onClick={handleAddToCart} disabled={Boolean(selectionError)}>
            Sepete Ekle ({quantity} adet)
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MenuItemCard;
